import { useState } from "react";
import { ChevronDown, ChevronRight, Users, Truck, Scale } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { OrganizationMember } from "@/lib/api/betterAuth";
import { CustomerAssignmentEditor } from "./CustomerAssignmentEditor";
import { cn } from "@/lib/utils";

interface CustomerPermissionsModalProps {
  open: boolean;
  onClose: () => void;
  member: OrganizationMember | null;
}

type SectionKey = "risk" | "freight" | "weight_ticket";

interface SectionConfig {
  key: SectionKey;
  title: string;
  icon: React.ReactNode;
  maxAllowed?: number;
}

const SECTIONS: SectionConfig[] = [
  {
    key: "risk",
    title: "Risk Profiles",
    icon: <Users className="h-4 w-4" />,
  },
  {
    key: "freight",
    title: "Freight Customers",
    icon: <Truck className="h-4 w-4" />,
    maxAllowed: 1,
  },
  {
    key: "weight_ticket",
    title: "Weight Ticket Customers",
    icon: <Scale className="h-4 w-4" />,
    maxAllowed: 1,
  },
];

export function CustomerPermissionsModal({
  open,
  onClose,
  member,
}: CustomerPermissionsModalProps) {
  const [openSections, setOpenSections] = useState<Set<SectionKey>>(
    new Set(["risk"])
  );

  const toggleSection = (key: SectionKey) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const getRelationsForType = (relationType: SectionKey) => {
    if (!member?.relations) return [];
    return member.relations.filter((rel) => rel.relation_type === relationType);
  };

  const getAssignedLabel = (relationType: SectionKey): string => {
    const relations = getRelationsForType(relationType);
    const count = relations.length;
    if (count === 0) return "0 assigned";
    if (count === 1) {
      const name = relations[0].Customer?.long_name;
      if (name) return name;
    }
    return `${count} assigned`;
  };

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Customer Permissions</DialogTitle>
          <p className="text-sm text-gray-600">
            {member.authuser?.name || "Unknown User"}
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pr-2">
          {SECTIONS.map((section) => {
            const isOpen = openSections.has(section.key);
            const assignedLabel = getAssignedLabel(section.key);

            return (
              <Collapsible
                key={section.key}
                open={isOpen}
                onOpenChange={() => toggleSection(section.key)}
              >
                <CollapsibleTrigger className="w-full">
                  <div
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg border transition-colors",
                      isOpen
                        ? "bg-gray-50 border-gray-300"
                        : "bg-white border-gray-200 hover:bg-gray-50"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      )}
                      <span className="text-gray-600">{section.icon}</span>
                      <span className="font-medium">{section.title}</span>
                    </div>
                    <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                      {assignedLabel}
                    </span>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="mt-2 p-4 border rounded-lg bg-white">
                    <CustomerAssignmentEditor
                      member={member}
                      relationType={section.key}
                      inline={false}
                      maxAllowed={section.maxAllowed}
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
